import { useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useActiveFeatureFlags } from '@posthog/react';
import { useHomes } from '@/contexts/HomesContext';
import { useGroups } from '@/contexts/GroupsContext';
import { postHogService } from '@/lib/posthog';
import type { FeatureFlagKey } from '@/lib/feature-flag-constants';
import {
    NAV_PREFS_PANES,
    NAV_SECONDARY,
    NAV_SECTIONS,
    workspaceRoot,
    type NavPrefsPane,
    type NavSecondaryItem,
    type NavSection,
    type NavStage,
} from './navConfig';

export interface Workspace {
    kind: 'home' | 'group';
    stage: NavStage;
    id: string;
    name: string;
    meta?: string;
    root: string;
}

export interface WorkspaceNav {
    workspace: Workspace | null;
    scoped: boolean;
    sections: NavSection[];
    secondary: NavSecondaryItem[];
    activeSectionId: string | null;
    prefsPanes: NavPrefsPane[];
    activePrefsPaneId: string | null;
    inPreferences: boolean;
    homes: Workspace[];
    groups: Workspace[];
}

const homeLabel = (home: { name?: string | null; address: string }) => home.name?.trim() || home.address;

const LAST_WORKSPACE_KEY = 'cohaven_last_workspace';

const SIDEBAR_KEEPING_ROUTES: string[] = [
    '/profile',
    '/groups',
    '/homes/invites',
    '/notifications',
];

const keepsSidebar = (pathname: string) =>
    SIDEBAR_KEEPING_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));

function recallWorkspace(allHomes: Workspace[], allGroups: Workspace[]): Workspace | null {
    let remembered: string | null = null;
    try {
        remembered = sessionStorage.getItem(LAST_WORKSPACE_KEY);
    } catch {
        remembered = null;
    }

    const candidates = [...allHomes, ...allGroups];
    return candidates.find((entry) => entry.id === remembered) ?? candidates[0] ?? null;
}

export function useWorkspaceNav(): WorkspaceNav {
    const { pathname } = useLocation();
    const params = useParams();
    const { homes } = useHomes();
    const { groups } = useGroups();
    const activeFlags = useActiveFeatureFlags();

    const nav = useMemo(() => {
        const enabledFlags = new Set(activeFlags ?? []);
        const isEnabled = (flag?: FeatureFlagKey) =>
            !postHogService.isEnabled || !flag || enabledFlags.has(flag);

        const allHomes: Workspace[] = homes.map((home) => ({
            kind: 'home',
            stage: 'manage',
            id: home.id,
            name: homeLabel(home),
            meta: [home.city, home.state].filter(Boolean).join(', '),
            root: workspaceRoot('manage', home.id),
        }));

        const allGroups: Workspace[] = groups.map((group) => ({
            kind: 'group',
            stage: 'explore',
            id: group.id,
            name: group.name,
            root: workspaceRoot('explore', group.id),
        }));

        let workspace: Workspace | null = null;
        if (params.homeId && pathname.startsWith('/home/')) {
            workspace = allHomes.find((entry) => entry.id === params.homeId) ?? null;
        } else if (params.groupId && pathname.startsWith('/groups/')) {
            workspace = allGroups.find((entry) => entry.id === params.groupId) ?? null;
        }

        const scoped = workspace !== null;

        if (!scoped && keepsSidebar(pathname)) {
            workspace = recallWorkspace(allHomes, allGroups);
        }

        if (!workspace) {
            return {
                workspace: null,
                scoped: false,
                sections: [],
                secondary: [],
                activeSectionId: null,
                prefsPanes: [],
                activePrefsPaneId: null,
                inPreferences: false,
                homes: allHomes,
                groups: allGroups,
            };
        }

        const { stage, root } = workspace;
        const sections = NAV_SECTIONS[stage].filter((section) => isEnabled(section.flag));
        const prefsPanes = NAV_PREFS_PANES[stage].filter((pane) => isEnabled(pane.flag));
        const rest = scoped ? pathname.slice(root.length).replace(/^\/+|\/+$/g, '') : ' ';

        const covers = (prefix: string) =>
            prefix === '' ? rest === '' : rest === prefix || rest.startsWith(`${prefix}/`);

        const activeSectionId =
            [...sections]
                .sort((a, b) => b.path.length - a.path.length)
                .find((section) => covers(section.path) || section.childPaths?.some(covers))?.id ?? null;

        const inPreferences = covers('preferences');
        const activePrefsPaneId = inPreferences
            ? (prefsPanes.find((pane) => rest === `preferences/${pane.path}`)?.id ?? null)
            : null;

        return {
            workspace,
            scoped,
            sections,
            secondary: NAV_SECONDARY[stage].filter((item) => isEnabled(item.flag)),
            activeSectionId,
            prefsPanes,
            activePrefsPaneId,
            inPreferences,
            homes: allHomes,
            groups: allGroups,
        };
    }, [pathname, params.homeId, params.groupId, homes, groups, activeFlags]);

    // to wherever the user actually was.
    const scopedWorkspaceId = nav.scoped ? nav.workspace?.id : undefined;
    useEffect(() => {
        if (!scopedWorkspaceId) return;
        try {
            sessionStorage.setItem(LAST_WORKSPACE_KEY, scopedWorkspaceId);
        } catch {
            /* storage unavailable — the sidebar falls back to the first workspace */
        }
    }, [scopedWorkspaceId]);

    return nav;
}
